import {
    Edit as EditIcon,
    MenuBookOutlined as MenuBookOutlinedIcon,
    Notifications as NotificationsIcon,
    PictureAsPdf as PictureAsPdfIcon,
    MusicNote as MusicNoteIcon,
    VolumeUp as VolumeUpIcon
} from "@mui/icons-material";
import Logger from "../../../services/Logger";
import {
    CircularProgress,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from "@mui/material";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import React, { useEffect, useState } from "react";
import { trackEvent } from "../../../services/GoogleAnalytics";
import GeoService from "../../../services/GeoService";
import { SUPPORT_FRENCH } from "../../../config/supportFrench";
import { CONTENT_LANG_KEY, getContentLanguage, getInterfaceLanguage, getOrderedContentLanguages, getOrderedLanguages, INTERFACE_LANG_KEY, LANGUAGES, setContentLanguage, setInterfaceLanguage } from "../../../services/LangPrefs";
import { usePagesTranslation } from "../../locales";
import UserService from "../../../services/UserService";
import { writeBgmAutoPlayPreference } from "../../components/bgmPreference";
import WebPushService from "../../../services/WebPushService";
import { PDF_DOWNLOAD_DICTIONARY_KEY, PDF_FORMAT_DEFAULT_KEY } from "../../../admin/pzl_common/PdfFormatDialog";

/**
 * SettingsMain Component
 * 
 * Main settings page where users can configure their application preferences.
 * Handles user settings including notifications, manual validation mode, and language.
 * All settings are persisted to the backend and synced with the user profile.
 */
const SettingsMain = () => {
    const { t } = usePagesTranslation();
    // User data state
    const [user, setUser] = useState(null);
    
    // Loading and saving states for UI feedback
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isQuebec, setIsQuebec] = useState(false);

    useEffect(() => {
        trackEvent('entered', { name: 'SettingsMain' });
    }, []);
    
    // Application settings state
    // Default values are used until user data is loaded
    const [settings, setSettings] = useState({
        notifications: true,        // Enable/disable notifications for puzzles and achievements
        uiLang: 'english',     // UI language ('english', 'french', 'korean', 'japanese', 'german', 'portuguese') - for interface, not puzzle content
        contentLang: 'english', // Content language ('english', 'french') - for puzzle content
        manualValidation: false,   // Enable manual word validation mode (type words instead of clicking)
        soundEnabled: false,        // Enable/disable sound effects when completing puzzles
        bgmAutoPlay: false,         // Auto-play background music on puzzle play pages
        pdfDictionary: false       // When true: PDF downloads use dictionary-style wording (omit display clues)
    });

    // Load user data and settings on component mount
    useEffect(() => {
        loadUserData();
        
        // Check Quebec status for language ordering
        const checkQuebec = async () => {
            try {
                const quebecStatus = await GeoService.isQuebec();
                setIsQuebec(quebecStatus);
            } catch (error) {
                Logger.error("Error detecting region:", error);
                setIsQuebec(false);
            }
        };
        checkQuebec();
    }, []);

    // Listen for localStorage changes (from other tabs/windows or other parts of the app)
    useEffect(() => {
        const handleStorageChange = (e) => {
            // Check if the changed key is one of our language preferences
            if (e.key === INTERFACE_LANG_KEY) {
                const newUiLang = getInterfaceLanguage();
                setSettings(prev => ({
                    ...prev,
                    uiLang: newUiLang
                }));
            } else if (e.key === CONTENT_LANG_KEY) {
                const newContentLang = getContentLanguage();
                setSettings(prev => ({
                    ...prev,
                    contentLang: newContentLang
                }));
            }
        };

        // Listen for storage events (changes from other tabs/windows)
        window.addEventListener('storage', handleStorageChange);

        // Also check for changes within the same window using a custom event
        // This handles cases where localStorage is changed programmatically in the same window
        const checkLocalStorageChanges = () => {
            const currentUiLang = getInterfaceLanguage();
            const currentContentLang = getContentLanguage();
            
            setSettings(prev => {
                const updates = {};
                if (prev.uiLang !== currentUiLang) {
                    updates.uiLang = currentUiLang;
                }
                if (prev.contentLang !== currentContentLang) {
                    updates.contentLang = currentContentLang;
                }
                
                // Only update if there are changes
                if (Object.keys(updates).length > 0) {
                    return { ...prev, ...updates };
                }
                return prev;
            });
        };

        // Check for changes periodically (every 1 second)
        // This catches changes made programmatically in the same window
        const intervalId = setInterval(checkLocalStorageChanges, 1000);

        // Cleanup
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(intervalId);
        };
    }, []);

    /**
     * Loads user information and settings from the backend
     * Extracts user profile and settings data, then updates component state
     * UI language is loaded from LangPrefs (interface language), not from user profile
     */
    const loadUserData = async () => {
        try {
            setLoading(true);
            const userInfo = await UserService.getUserInfo();
            setUser(userInfo);
            
            // Extract settings from user profile data
            const settingsData = UserService.getSettingsFormData(userInfo);
            
            // Override language setting with interface language from LangPrefs (UI language, not puzzle content)
            // Use full language names directly from LangPrefs
            const interfaceLang = getInterfaceLanguage();
            settingsData.uiLang = interfaceLang;
            
            // Load content language from LangPrefs (for puzzle content)
            const contentLang = getContentLanguage();
            settingsData.contentLang = contentLang;
            
            if (typeof window !== "undefined") {
                if (settingsData.pdfFormat) {
                    window.localStorage.setItem(PDF_FORMAT_DEFAULT_KEY, settingsData.pdfFormat);
                }
                window.localStorage.setItem(
                    PDF_DOWNLOAD_DICTIONARY_KEY,
                    settingsData.pdfDictionary === true ? "true" : "false"
                );
            }
            setSettings(settingsData);
            if (typeof settingsData.bgmAutoPlay === "boolean") {
                writeBgmAutoPlayPreference(settingsData.bgmAutoPlay);
            }
            WebPushService.syncFromPreference(settingsData.notifications !== false, {
                requestPermission: false,
            }).catch((error) => Logger.error("[WebPush] sync on load failed", error));
        } catch (error) {
            Logger.error('Error loading user data:', error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handles changes to any setting
     * 
     * Updates the local state immediately for responsive UI, then persists to backend.
     * If the save operation fails, the change is reverted to the previous value.
     * 
     * @param {string} setting - The name of the setting being changed (e.g., 'notifications')
     * @returns {Function} Event handler function
     */
    const handleSettingChange = (setting) => async (event) => {
        // Extract new value from event (handles both checkbox and select inputs)
        const newValue = event.target.type === 'checkbox' ? event.target.checked : event.target.value;

        if (setting === "pdfDictionary" && typeof window !== "undefined") {
            window.localStorage.setItem(PDF_DOWNLOAD_DICTIONARY_KEY, newValue ? "true" : "false");
        }

        // Update local state immediately for responsive UI feedback
        setSettings(prev => ({
            ...prev,
            [setting]: newValue
        }));

        if (setting === "bgmAutoPlay") {
            writeBgmAutoPlayPreference(Boolean(newValue));
        }

        // If UI language setting changed, update interface language in LangPrefs immediately
        if (setting === 'uiLang') {
            setInterfaceLanguage(newValue);
        }
        
        // If content language setting changed, update content language in LangPrefs immediately
        if (setting === 'contentLang') {
            setContentLanguage(newValue);
        }

        // Persist changes to backend
        if (user?.cloudUser?.id) {
            try {
                setSaving(true);
                const updatedSettings = {
                    ...settings,
                    [setting]: newValue
                };

                // Validate settings before saving
                const validation = validateSettingsData(updatedSettings);
                if (!validation.isValid) {
                    Logger.error('Settings validation failed:', validation.errors);
                    return;
                }

                // Save validated settings to backend
                if (setting === "notifications") {
                    await UserService.updateUserProfile(
                        user.cloudUser.id,
                        UserService.notificationsByAppPayload(Boolean(newValue))
                    );
                    await WebPushService.syncFromPreference(Boolean(newValue), {
                        requestPermission: Boolean(newValue),
                    });
                } else {
                    await UserService.updateUserProfile(user.cloudUser.id, updatedSettings);
                }
            } catch (error) {
                Logger.error('Error saving settings:', error);
                if (setting === "pdfDictionary" && typeof window !== "undefined") {
                    window.localStorage.setItem(
                        PDF_DOWNLOAD_DICTIONARY_KEY,
                        settings.pdfDictionary ? "true" : "false"
                    );
                }
                // Revert the change on error to maintain data consistency
                setSettings(prev => ({
                    ...prev,
                    [setting]: !newValue
                }));
                // Also revert LangPrefs if UI language was changed
                if (setting === 'uiLang') {
                    setInterfaceLanguage(settings.uiLang);
                }
                if (setting === 'contentLang') {
                    setContentLanguage(settings.contentLang);
                }
            } finally {
                setSaving(false);
            }
        } else if (setting === 'uiLang') {
            // Even if user is not logged in, save interface language preference locally
            setInterfaceLanguage(newValue);
        } else if (setting === 'contentLang') {
            // Even if user is not logged in, save content language preference locally
            setContentLanguage(newValue);
        }
    };

    const handleContentLanguageChange = async (_event, newLanguage) => {
        if (newLanguage !== null) {
            // Update local state immediately
            setSettings(prev => ({
                ...prev,
                contentLang: newLanguage
            }));
            
            // Update content language in LangPrefs immediately
            setContentLanguage(newLanguage);
            
            // Call updateUserProfile if user is logged in
            if (user?.cloudUser?.id) {
                try {
                    setSaving(true);
                    const updatedSettings = {
                        ...settings,
                        contentLang: newLanguage
                    };
                    
                    // Validate settings before saving
                    const validation = validateSettingsData(updatedSettings);
                    if (!validation.isValid) {
                        Logger.error('Settings validation failed:', validation.errors);
                        return;
                    }
                    
                    // Save validated settings to backend
                    await UserService.updateUserProfile(user.cloudUser.id, updatedSettings);
                } catch (error) {
                    Logger.error('Error saving content language:', error);
                    // Revert the change on error
                    setSettings(prev => ({
                        ...prev,
                        contentLang: settings.contentLang
                    }));
                    setContentLanguage(settings.contentLang);
                } finally {
                    setSaving(false);
                }
            }
        }
    };

    const handleUiLanguageChange = (_event, newLanguage) => {
        if (newLanguage !== null) {
            const changeEvent = {
                target: {
                    type: 'select',
                    value: newLanguage
                }
            };
            handleSettingChange('uiLang')(changeEvent);
        }
    };

    const handlePdfFormatChange = async (_event, newFormat) => {
        if (newFormat !== null) {
            setSettings(prev => ({ ...prev, pdfFormat: newFormat }));
            if (typeof window !== "undefined") {
                window.localStorage.setItem(PDF_FORMAT_DEFAULT_KEY, newFormat);
            }
            if (user?.cloudUser?.id) {
                try {
                    setSaving(true);
                    const updatedSettings = { ...settings, pdfFormat: newFormat };
                    const validation = validateSettingsData(updatedSettings);
                    if (!validation.isValid) return;
                    await UserService.updateUserProfile(user.cloudUser.id, updatedSettings);
                } catch (error) {
                    Logger.error("Error saving PDF format:", error);
                    setSettings(prev => ({ ...prev, pdfFormat: settings.pdfFormat }));
                    if (typeof window !== "undefined") {
                        window.localStorage.setItem(PDF_FORMAT_DEFAULT_KEY, settings.pdfFormat);
                    }
                } finally {
                    setSaving(false);
                }
            }
        }
    };


    /**
     * Validates settings data before saving
     * 
     * Ensures all settings have valid types and values.
     * Validates language against allowed values and boolean fields against boolean type.
     * 
     * @param {Object} settingsData - The settings object to validate
     * @returns {Object} Validation result with isValid flag and errors array
     */
    const validateSettingsData = (settingsData) => {
        const errors = [];
        const validLanguages = LANGUAGES.map(l => l.code);
        const validContentLanguages = ['english', 'french', 'spanish', 'german', 'portuguese', 'chinese', 'japanese', 'korean', 'arabic', 'hindi'];

        // Validate UI language selection if provided
        if (settingsData.uiLang && !validLanguages.includes(settingsData.uiLang)) {
            errors.push('Invalid UI language selection');
        }

        // Validate content language selection if provided
        if (settingsData.contentLang && !validContentLanguages.includes(settingsData.contentLang)) {
            errors.push('Invalid content language selection');
        }

        // Validate boolean settings
        // Only validate if the property exists in the data object
        if (settingsData.hasOwnProperty('notifications') && typeof settingsData.notifications !== 'boolean') {
            errors.push('Notifications setting must be boolean');
        }

        if (settingsData.hasOwnProperty('manualValidation') && typeof settingsData.manualValidation !== 'boolean') {
            errors.push('Manual validation setting must be boolean');
        }

        if (settingsData.hasOwnProperty('soundEnabled') && typeof settingsData.soundEnabled !== 'boolean') {
            errors.push('Sound enabled setting must be boolean');
        }

        if (settingsData.hasOwnProperty('bgmAutoPlay') && typeof settingsData.bgmAutoPlay !== 'boolean') {
            errors.push('Background music auto-play setting must be boolean');
        }

        if (settingsData.hasOwnProperty('pdfDictionary') && typeof settingsData.pdfDictionary !== 'boolean') {
            errors.push('PDF dictionary setting must be boolean');
        }

        if (settingsData.pdfFormat != null && settingsData.pdfFormat !== '' && !['letter', 'a4'].includes(settingsData.pdfFormat)) {
            errors.push('PDF format must be letter or a4');
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    };

    /**
     * Configuration for setting items displayed in the General Settings section
     * Each item defines the UI presentation and the setting key to update
     */
    const settingItems = [
        {
            icon: <NotificationsIcon />,
            primaryKey: "profile.settings.notifications",
            secondaryKey: "profile.settings.notifications-desc",
            type: "switch",
            setting: "notifications"
        },
        {
            icon: <VolumeUpIcon />,
            primaryKey: "profile.settings.sound",
            secondaryKey: "profile.settings.sound-desc",
            type: "switch",
            setting: "soundEnabled"
        },
        {
            icon: <MusicNoteIcon />,
            primaryKey: "profile.settings.bgm-auto-play",
            secondaryKey: "profile.settings.bgm-auto-play-desc",
            type: "switch",
            setting: "bgmAutoPlay"
        },
        {
            icon: <PictureAsPdfIcon />,
            primaryKey: "profile.settings.pdf-format",
            secondaryKey: "profile.settings.pdf-format-desc",
            type: "toggleGroup",
            setting: "pdfFormat"
        },
        {
            icon: <MenuBookOutlinedIcon />,
            primaryKey: "profile.settings.pdf-dictionary",
            secondaryKey: "profile.settings.pdf-dictionary-desc",
            tooltipKey: "profile.settings.pdf-dictionary-tooltip",
            type: "switch",
            setting: "pdfDictionary"
        },
        {
            icon: <EditIcon />,
            primaryKey: "profile.settings.manual-validation",
            secondaryKey: "profile.settings.manual-validation-desc",
            type: "switch",
            setting: "manualValidation",
            roles: ['CREATOR', 'ADMIN'] // Only visible to CREATOR and ADMIN
        }
    ];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                {t("profile.settings.title")}
            </Typography>

            <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
                {/* Language column: puzzle content language (when French is on) stacks above the
                    interface language, so general settings keeps the other half of the row. */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {SUPPORT_FRENCH ? (
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    {t("profile.settings.content-lang")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {t("profile.settings.content-lang-desc")}
                                </Typography>
                                <ToggleButtonGroup
                                    value={settings.contentLang}
                                    exclusive
                                    onChange={handleContentLanguageChange}
                                    disabled={saving}
                                    sx={{ mt: 2 }}
                                >
                                    {getOrderedContentLanguages(isQuebec).map((lang) => (
                                        <ToggleButton
                                            key={lang.code}
                                            value={lang.code}
                                            sx={{
                                                '&.Mui-selected': {
                                                    borderColor: '#3BA666 !important',
                                                    borderWidth: '2px !important',
                                                    borderStyle: 'solid !important',
                                                    borderLeftColor: '#3BA666 !important',
                                                    borderRightColor: '#3BA666 !important',
                                                    borderTopColor: '#3BA666 !important',
                                                    borderBottomColor: '#3BA666 !important',
                                                    backgroundColor: 'rgba(59, 166, 102, 0.08)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(59, 166, 102, 0.12)'
                                                    }
                                                }
                                            }}
                                        >
                                            {lang.name}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Paper>
                        ) : null}

                        {/* Allows users to select their preferred UI language (interface language, not puzzle content) */}
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                {t("profile.settings.ui-lang")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t("profile.settings.ui-lang-desc")}
                            </Typography>
                            <ToggleButtonGroup
                                value={settings.uiLang}
                                exclusive
                                onChange={handleUiLanguageChange}
                                disabled={saving}
                                sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}
                            >
                                {getOrderedLanguages(isQuebec).map((lang) => (
                                    <ToggleButton
                                        key={lang.code}
                                        value={lang.code}
                                        sx={{
                                            '&.Mui-selected': {
                                                borderColor: '#3BA666 !important',
                                                borderWidth: '2px !important',
                                                borderStyle: 'solid !important',
                                                borderLeftColor: '#3BA666 !important',
                                                borderRightColor: '#3BA666 !important',
                                                borderTopColor: '#3BA666 !important',
                                                borderBottomColor: '#3BA666 !important',
                                                backgroundColor: 'rgba(59, 166, 102, 0.08)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(59, 166, 102, 0.12)'
                                                }
                                            }
                                        }}
                                    >
                                        {lang.name}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Paper>
                    </Box>
                </Grid>

                {/* General Settings Section */}
                {/* Contains toggle switches for notifications, hints, and manual validation */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            {t("profile.settings.general")}
                        </Typography>
                        <List>
                            {settingItems
                                .filter(item => {
                                    // If item has roles restriction, check user role
                                    if (item.roles) {
                                        const userRole = user?.cloudUser?.userRole;
                                        return userRole && item.roles.includes(userRole);
                                    }
                                    // Otherwise, show to all users
                                    return true;
                                })
                                .map((item, index, filteredItems) => {
                                    const userRole = user?.cloudUser?.userRole;
                                    const isManualValidation = item.setting === 'manualValidation';
                                    const showCreatorMessage = isManualValidation && (userRole === 'CREATOR' || userRole === 'ADMIN');
                                    
                                    return (
                                        <React.Fragment key={index}>
                                            {showCreatorMessage && (
                                                <>
                                                    <ListItem>
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#3BA666', fontWeight: 500 }}>
                                                                    {t("profile.settings.creator-menu-note", { role: userRole })}
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItem>
                                                    <Divider />
                                                </>
                                            )}
                                            <ListItem>
                                                <ListItemIcon sx={{ color: isManualValidation && showCreatorMessage ? '#3BA666' : 'inherit' }}>
                                                    {item.icon}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        item.tooltipKey ? (
                                                            <Tooltip title={t(item.tooltipKey)} arrow placement="top">
                                                                <Typography
                                                                    component="span"
                                                                    sx={{
                                                                        color:
                                                                            isManualValidation && showCreatorMessage
                                                                                ? "#3BA666"
                                                                                : "inherit",
                                                                        fontWeight:
                                                                            isManualValidation && showCreatorMessage
                                                                                ? 500
                                                                                : "inherit",
                                                                        cursor: "help",
                                                                    }}
                                                                >
                                                                    {t(item.primaryKey)}
                                                                </Typography>
                                                            </Tooltip>
                                                        ) : (
                                                            <Typography
                                                                sx={{
                                                                    color:
                                                                        isManualValidation && showCreatorMessage
                                                                            ? "#3BA666"
                                                                            : "inherit",
                                                                    fontWeight:
                                                                        isManualValidation && showCreatorMessage
                                                                            ? 500
                                                                            : "inherit",
                                                                }}
                                                            >
                                                                {t(item.primaryKey)}
                                                            </Typography>
                                                        )
                                                    }
                                                    secondary={t(item.secondaryKey)}
                                                />
                                                {item.type === "switch" && (
                                                    <Switch
                                                        checked={settings[item.setting]}
                                                        onChange={handleSettingChange(item.setting)}
                                                        color="primary"
                                                        disabled={saving}
                                                    />
                                                )}
                                                {item.type === "toggleGroup" && item.setting === "pdfFormat" && (
                                                    <ToggleButtonGroup
                                                        value={settings.pdfFormat ?? ''}
                                                        exclusive
                                                        onChange={handlePdfFormatChange}
                                                        disabled={saving}
                                                        size="small"
                                                        sx={{
                                                            flexDirection: { xs: 'column', sm: 'row' },
                                                            "& .MuiToggleButton-root": {
                                                                flex: { xs: '0 0 auto', sm: 1 },
                                                                minWidth: 0,
                                                                px: 2,
                                                                width: { xs: '100%', sm: 'auto' }
                                                            },
                                                            "& .Mui-selected": {
                                                                borderColor: "#3BA666 !important",
                                                                borderWidth: "2px !important",
                                                                borderStyle: "solid !important",
                                                                backgroundColor: "rgba(59, 166, 102, 0.08)",
                                                                "&:hover": { backgroundColor: "rgba(59, 166, 102, 0.12)" }
                                                            }
                                                        }}
                                                    >
                                                        <ToggleButton value="letter">{t("profile.settings.pdf-letter")}</ToggleButton>
                                                        <ToggleButton value="a4">{t("profile.settings.pdf-a4")}</ToggleButton>
                                                    </ToggleButtonGroup>
                                                )}
                                            </ListItem>
                                            {/* Add divider between items, but not after the last one */}
                                            {index < filteredItems.length - 1 && <Divider />}
                                        </React.Fragment>
                                    );
                                })}
                        </List>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SettingsMain;
